import { useState } from 'react'
import {
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress
} from '@mui/material'
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh'
import EditNoteIcon from '@mui/icons-material/EditNote'
import ExpandIcon from '@mui/icons-material/UnfoldMore'
import CompressIcon from '@mui/icons-material/UnfoldLess'
import SpellcheckIcon from '@mui/icons-material/Spellcheck'
import ChatIcon from '@mui/icons-material/Chat'
import AccountTreeIcon from '@mui/icons-material/AccountTree'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import MDEditor from '@uiw/react-md-editor'
import { useTheme } from '../contexts/ThemeContext'
import { colors } from '../styles/theme'

const API_URL = import.meta.env.VITE_API_URL || '/api'

const ACTIONS = [
  { key: 'polish', label: 'Polish & Refine', icon: <EditNoteIcon fontSize="small" />, description: 'Improve clarity, flow, and tone' },
  { key: 'expand', label: 'Expand', icon: <ExpandIcon fontSize="small" />, description: 'Add more detail and examples' },
  { key: 'shorten', label: 'Shorten', icon: <CompressIcon fontSize="small" />, description: 'Cut to half length, keep key points' },
  { key: 'grammar', label: 'Fix Grammar', icon: <SpellcheckIcon fontSize="small" />, description: 'Fix spelling and punctuation only' },
  { key: 'conversational', label: 'Make Conversational', icon: <ChatIcon fontSize="small" />, description: 'Rewrite in a friendlier tone' },
  { key: 'structure', label: 'Improve Structure', icon: <AccountTreeIcon fontSize="small" />, description: 'Add headings, break up paragraphs' },
]

const AIEnhance = ({ content, title, onAccept }) => {
  const { isDark } = useTheme()
  const [anchorEl, setAnchorEl] = useState(null)
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState(null)
  const [activeAction, setActiveAction] = useState(null)

  const handleAction = async (action) => {
    setAnchorEl(null)
    setLoading(true)
    setActiveAction(action)

    try {
      const response = await fetch(`${API_URL}/ai/enhance`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content, title, action: action.key }),
      })

      if (!response.ok) {
        const err = await response.json()
        throw new Error(err.error || 'Enhancement failed')
      }

      const data = await response.json()
      setResult(data.enhanced)
    } catch (error) {
      console.error('AI enhance error:', error)
      setResult(null)
      setActiveAction(null)
      alert(error.message || 'Failed to enhance content')
    } finally {
      setLoading(false)
    }
  }

  const handleAccept = () => {
    onAccept(result)
    setResult(null)
    setActiveAction(null)
  }

  const handleReject = () => {
    setResult(null)
    setActiveAction(null)
  }

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        startIcon={loading ? <CircularProgress size={16} sx={{ color: colors.secondaryLight }} /> : <AutoFixHighIcon />}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        disabled={loading || !content?.trim()}
        sx={{
          borderColor: colors.secondaryLight,
          color: colors.secondaryLight,
          '&:hover': { borderColor: colors.secondaryLight, backgroundColor: 'rgba(var(--color-accent-secondary-rgb), 0.1)' },
        }}
      >
        {loading ? 'Enhancing...' : 'AI Enhance'}
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        PaperProps={{
          sx: {
            backgroundColor: colors.bgCard,
            color: colors.textPrimary,
            border: `1px solid rgba(var(--color-accent-secondary-rgb), 0.2)`,
            minWidth: 240,
          }
        }}
      >
        {ACTIONS.map((action) => (
          <MenuItem
            key={action.key}
            onClick={() => handleAction(action)}
            sx={{
              '&:hover': { backgroundColor: 'rgba(var(--color-accent-secondary-rgb), 0.1)' },
              py: 1.5,
            }}
          >
            <ListItemIcon sx={{ color: colors.secondaryLight }}>
              {action.icon}
            </ListItemIcon>
            <ListItemText
              primary={action.label}
              secondary={action.description}
              primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 600 }}
              secondaryTypographyProps={{ fontSize: '0.75rem', color: colors.textMuted }}
            />
          </MenuItem>
        ))}
      </Menu>

      {/* Diff / Preview Dialog */}
      <Dialog
        open={result !== null}
        onClose={handleReject}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: colors.bgPage,
            color: colors.textPrimary,
            maxHeight: '90vh',
          }
        }}
      >
        <DialogTitle sx={{ borderBottom: `1px solid rgba(var(--color-accent-secondary-rgb), 0.2)` }}>
          <div className="flex items-center gap-2">
            <AutoFixHighIcon sx={{ color: colors.secondaryLight }} />
            <span>AI Suggestion — {activeAction?.label}</span>
          </div>
          <p className="text-gray-400 text-sm mt-1 font-normal">
            Review the enhanced version below. Accept to replace your content, or reject to keep the original.
          </p>
        </DialogTitle>
        <DialogContent sx={{ p: 0, mt: 2 }}>
          <div className="grid md:grid-cols-2 gap-0 h-full">
            {/* Original */}
            <div className="border-r border-gray-700">
              <div className="px-4 py-2 bg-slate/50 border-b border-gray-700">
                <span className="text-sm font-mono text-gray-400">Original</span>
              </div>
              <div className="p-4 overflow-auto max-h-[60vh]" data-color-mode={isDark ? 'dark' : 'light'}>
                <MDEditor.Markdown source={content} />
              </div>
            </div>
            {/* Enhanced */}
            <div>
              <div className="px-4 py-2 bg-purple/10 border-b border-gray-700">
                <span className="text-sm font-mono text-purple">Enhanced</span>
              </div>
              <div className="p-4 overflow-auto max-h-[60vh]" data-color-mode={isDark ? 'dark' : 'light'}>
                <MDEditor.Markdown source={result || ''} />
              </div>
            </div>
          </div>
        </DialogContent>
        <DialogActions sx={{ borderTop: `1px solid rgba(var(--color-accent-secondary-rgb), 0.2)`, p: 2 }}>
          <Button
            onClick={handleReject}
            startIcon={<CloseIcon />}
            sx={{ color: colors.textMuted }}
          >
            Reject
          </Button>
          <Button
            onClick={handleAccept}
            variant="contained"
            startIcon={<CheckIcon />}
            sx={{
              backgroundColor: colors.accent,
              color: colors.textOnAccent,
              fontWeight: 700,
              '&:hover': { backgroundColor: colors.accentHover },
            }}
          >
            Accept
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default AIEnhance
